// Pure helpers for the cliente 360 Servicios tab (cards + mapa de pagos).
// No React here: every rule (geolocation filter, estado summary, map
// geometry, cluster threshold) is unit-testable.
import dayjs from 'dayjs';
import { formatCurrency } from '../../lib/format';
import { esCoordenadaValida } from '../../lib/geo';
import { tonoEstadoServicio } from '../../lib/estado-servicio-tono';
import type { StatusTagTone } from '../../components/StatusTag';
import type { PagoClienteResponse, ServicioResponse } from '@aid/shared';

export { esCoordenadaValida } from '../../lib/geo';
export { tonoEstadoServicio } from '../../lib/estado-servicio-tono';

const FECHA_FORMATO = 'DD/MM/YYYY';

/** Pago listo para el mapa: coordenadas validadas y campos del popup. */
export interface PagoGeolocalizado {
  id: number;
  latitud: number;
  longitud: number;
  fechaPago: Date;
  valor: string;
}

export interface ResumenEstadoServicio {
  /** Key del catálogo ESTADO_SERVICIO normalizada (minúsculas) o 'sin-estado'. */
  clave: string;
  /** Etiqueta del catálogo (value ?? key) para la chip del resumen. */
  etiqueta: string;
  cantidad: number;
  tono: StatusTagTone;
}

export interface ResumenServicios {
  total: number;
  porEstado: ResumenEstadoServicio[];
}

export interface BoundsGeo {
  sur: number;
  norte: number;
  oeste: number;
  este: number;
}

/**
 * Coordenada GPS válida: número finito distinto de cero.
 * Acepta number y string numérico (el backend serializa los numeric de
 * Postgres como string, igual que `valor`). Implementación compartida en
 * src/lib/geo.ts (usada también por el botón "Ver en mapa").
 */

/** Pagos del cliente con latitud y longitud válidas (GPS capturado en la app legacy). */
export function pagosConCoordenadas(pagos: PagoClienteResponse[]): PagoGeolocalizado[] {
  const result: PagoGeolocalizado[] = [];
  for (const p of pagos) {
    if (!esCoordenadaValida(p.latitud) || !esCoordenadaValida(p.longitud)) continue;
    result.push({
      id: p.id,
      latitud: Number(p.latitud),
      longitud: Number(p.longitud),
      fechaPago: p.fechaPago,
      valor: p.valor,
    });
  }
  return result;
}

/** Conteo por estado: total + chips ordenadas por cantidad desc y etiqueta. */
export function resumenEstados(servicios: ServicioResponse[]): ResumenServicios {
  const conteo = new Map<string, { clave: string; etiqueta: string; cantidad: number }>();
  for (const s of servicios) {
    const clave = s.estado?.key?.toLowerCase() ?? 'sin-estado';
    const etiqueta = s.estado?.value ?? s.estado?.key ?? 'N/A';
    const actual = conteo.get(clave);
    if (actual) {
      actual.cantidad += 1;
    } else {
      conteo.set(clave, { clave, etiqueta, cantidad: 1 });
    }
  }
  const porEstado = [...conteo.values()]
    .sort((a, b) => b.cantidad - a.cantidad || a.etiqueta.localeCompare(b.etiqueta))
    .map((e) => ({ ...e, tono: tonoEstadoServicio(e.clave) }));
  return { total: servicios.length, porEstado };
}

/**
 * Suma de los precios de plan de los servicios ACTIVOS, formateada como
 * moneda (null cuando no hay activos o ninguno tiene precio). El precio de
 * plan viaja como string ("14.00") igual que el valor de cobros/pagos.
 */
export function precioTotalActivos(servicios: ServicioResponse[]): string | null {
  let total = 0;
  let hayPrecio = false;
  for (const s of servicios) {
    if (s.estado?.key?.toLowerCase() !== 'activo') continue;
    const precio = Number(s.plan?.precio);
    if (!Number.isFinite(precio) || precio <= 0) continue;
    total += precio;
    hayPrecio = true;
  }
  return hayPrecio ? formatCurrency(total) : null;
}

/** Caja geográfica mínima de los puntos (null cuando no hay puntos). */
export function boundsDePuntos(puntos: PagoGeolocalizado[]): BoundsGeo | null {
  if (puntos.length === 0) return null;
  let sur = Infinity;
  let norte = -Infinity;
  let oeste = Infinity;
  let este = -Infinity;
  for (const p of puntos) {
    if (p.latitud < sur) sur = p.latitud;
    if (p.latitud > norte) norte = p.latitud;
    if (p.longitud < oeste) oeste = p.longitud;
    if (p.longitud > este) este = p.longitud;
  }
  return { sur, norte, oeste, este };
}

/**
 * Umbral a partir del cual los pagos se dibujan como mapa de calor en vez
 * de marcadores puntuales. Con <10 pagos un heatmap es una mancha tenue
 * sin información; con 10+ (66% de los clientes) los pines se pisan y el
 * calor comunica las áreas reales de cobranza sin recargar el mapa.
 */
export const UMBRAL_HEATMAP = 10;

export function debeUsarHeatmap(cantidad: number): boolean {
  return cantidad >= UMBRAL_HEATMAP;
}

/** Mezcla un color hex (#rgb/#rrggbb) con `objetivo` (blanco/negro) por porcentaje. */
export function mezclarHex(color: string, objetivo: string, pct: number): string {
  const parse = (c: string): [number, number, number] => {
    let hex = c.replace('#', '');
    if (hex.length === 3)
      hex = hex
        .split('')
        .map((x) => x + x)
        .join('');
    const n = parseInt(hex, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const [r1, g1, b1] = parse(color);
  const [r2, g2, b2] = parse(objetivo);
  const p = Math.min(1, Math.max(0, pct));
  const ch = (a: number, b: number) =>
    Math.round(a + (b - a) * p)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(r1, r2)}${ch(g1, g2)}${ch(b1, b2)}`;
}

/** Token hex con alpha; si no es hex devuelve el color sin alpha. */
export function conAlpha(color: string, alpha: number): string {
  const hex = color.replace('#', '');
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  if (hex.length === 3) {
    return `#${hex
      .split('')
      .map((c) => c + c)
      .join('')}${a}`;
  }
  if (hex.length === 6 && /^[0-9a-f]{6}$/i.test(hex)) {
    return `#${hex}${a}`;
  }
  return color;
}

/**
 * Gradiente del heatmap con degradado pronunciado: pálido → primario →
 * tono extremo, en vez de solo variar la opacidad (el ojo no distingue
 * alpha; sí distingue luminancia). En dark el tope se aclara con blanco;
 * en light el tope se oscurece con negro.
 */
export interface GradienteHeat {
  bajo: string;
  medio: string;
  alto: string;
  stops: Record<number, string>;
}

export function gradienteHeat(primary: string, modo: 'light' | 'dark'): GradienteHeat {
  if (modo === 'dark') {
    const bajo = conAlpha(primary, 0.4);
    const medio = conAlpha(primary, 0.9);
    const alto = conAlpha(mezclarHex(primary, '#ffffff', 0.3), 1);
    return { bajo, medio, alto, stops: { 0.25: bajo, 0.6: medio, 1: alto } };
  }
  const bajo = conAlpha(mezclarHex(primary, '#ffffff', 0.7), 0.85);
  const medio = conAlpha(primary, 0.95);
  const alto = conAlpha(mezclarHex(primary, '#000000', 0.4), 1);
  return { bajo, medio, alto, stops: { 0.25: bajo, 0.6: medio, 1: alto } };
}

/** Fecha corta DD/MM/YYYY para el popup del marcador. */
export function fechaPagoCorta(fecha: Date): string {
  return dayjs(fecha).format(FECHA_FORMATO);
}

/** Texto localizable del popup: fecha DD/MM/YYYY y monto formateado. */
export function formatoPagoPopup(pago: PagoGeolocalizado): { fecha: string; monto: string } {
  return {
    fecha: fechaPagoCorta(pago.fechaPago),
    monto: formatCurrency(pago.valor),
  };
}
